import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import {
  API,
  ca_am_User_retrieveAll_order,
  ManagedUserClientDTO,
  Order,
  RoleDTO,
  UserClientFullDTO,
  UserFullDTO,
} from '../../../services/api/api';
import { OrderOption, SplitButtonClass } from '../../../services/global/global-interfaces';
import { UserService } from '../../../services/user.service';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { ErrorService } from '../../../services/error.service';
import { UtilService } from '../../../services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { PermissionService } from '../../../services/permission.service';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { ToasterService, ToastType } from '../../../services/toaster/toaster.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NavigationService } from '@ngx-seatback/utils';
import { ClientUserEditModalComponent } from './edit/client-user-edit-modal.component';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RequestClass } from '../../../services/global/RequestClass';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { SbDateTime } from '../../../services/api/api-date-time';

type SplitButtonAction = 'block' | 'unblock';
type UserClientFullDTOAndButtons = SplitButtonClass<UserClientFullDTO, SplitButtonAction> & {
  extraButtons?: SbSplitButtonItem<SplitButtonAction>[];
};

@Component({
  selector: 'page-client-user-management',
  templateUrl: './client-user-management.component.html',
  styleUrls: ['./client-user-management.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientUserManagementComponent extends RequestClass implements OnInit {
  protected readonly api = inject(API);
  private readonly router = inject(Router);
  public readonly util = inject(UtilService);
  public readonly modal = inject(ModalService);
  public readonly userStatus = inject(UserService);
  public readonly errorHandler = inject(ErrorService);
  public readonly translate = inject(TranslateService);
  private readonly navService = inject(NavigationService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly toasterService = inject(ToasterService);
  public readonly permissionService = inject(PermissionService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly stringTransformation = inject(StringTransformationService);

  // reference variables
  protected readonly ca_am_User_retrieveAll_order = ca_am_User_retrieveAll_order;

  // order variables
  allOrderOptions: OrderOption<ca_am_User_retrieveAll_order>[] = [
    { value: ca_am_User_retrieveAll_order.USER_LASTNAME, direction: Order.asc, label: this.translate.instant('lang.globals.lastName') },
    { value: ca_am_User_retrieveAll_order.USER_FIRSTNAME, direction: Order.asc, label: this.translate.instant('lang.globals.firstName') },
    { value: ca_am_User_retrieveAll_order.USER_EMAIL, direction: Order.asc, label: this.translate.instant('lang.globals.email') },
  ];
  orderOption = signal({ ...this.allOrderOptions[0] });

  // global Variables
  users: UserClientFullDTOAndButtons[] = [];
  modalOpen: boolean = false;

  // filter panel variables
  filteredPermissions: string[] = [];
  filteredRoles: RoleDTO[] = [];
  search: string | null = null;
  exactMatch: SearchOptions = 'contains';

  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    button: {
      visibleWithoutChanges: true,
    },
  };

  ngOnInit(): void {
    // start router listener
    this.router.events.subscribe(e => {
      if ('routerEvent' in e && e.routerEvent instanceof NavigationEnd) {
        this.handleRouteModal();
      }
    });
    this.requestDebounced();
  }

  // order methods
  resetOrderOption() {
    // reset to default ordering, copy object properties to avoid changing original default
    this.orderOption.set({ ...this.allOrderOptions[0] });
  }

  getOrder(): {
    by: ca_am_User_retrieveAll_order;
    direction: Order;
  } {
    // if no orderOption -> return default order
    const currentOrderOption = this.orderOption();
    if (!currentOrderOption.value) {
      this.resetOrderOption();
    }
    // if value equals column title
    return {
      by: currentOrderOption.value,
      direction: currentOrderOption.direction,
    };
  }

  setToOrderAndRefresh(pValue: ca_am_User_retrieveAll_order) {
    this.setToOrderOption(pValue);
    this.requestDebounced();
  }

  setToOrderOption(pValue: ca_am_User_retrieveAll_order) {
    const foundOrderOption = this.allOrderOptions.find(option => option.value === pValue);
    if (!pValue || !foundOrderOption) {
      // fallback to default if nothing is set or orderOption cannot be found in allOrderOptions
      this.resetOrderOption();
      return;
    }
    const currentOrderOption = this.orderOption();
    if (currentOrderOption.value === pValue) {
      if (currentOrderOption.direction === Order.asc) {
        this.orderOption.set({ ...currentOrderOption, direction: Order.desc });
      } else {
        this.orderOption.set({ ...currentOrderOption, direction: Order.asc });
      }
    } else {
      this.orderOption.set({ ...foundOrderOption });
    }
  }

  // request methods
  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .ca_am_User_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          order: this.getOrder(),
          hasPermission: this.filteredPermissions?.length > 0 ? this.filteredPermissions : [],
          hasRole: this.util.arrayToIdArray(this.filteredRoles),
        },
        this.pagination
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (userClients: UserClientFullDTO[]) => {
          this.users = this.buildSplitButtons(userClients);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  buildSplitButtons<T extends { id: any; blocked: SbDateTime; user: UserFullDTO }>(
    itemsToAdd: T[]
  ): SplitButtonClass<T, SplitButtonAction>[] {
    const result = itemsToAdd.map(item => {
      return {
        ...item,
        sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[],
        extraButtons: [] as SbSplitButtonItem<SplitButtonAction>[],
      };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.client_user_unblock])) {
        item.extraButtons.push({
          icon: 'success',
          tooltip: 'lang.userEdit.unblockUser',
          action: 'unblock',
          iconClass: 'hover:!tw-bg-sbGreen-500 *:tw-text-sbGreen-500',
          disabled: !item.blocked,
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.client_user_block])) {
        item.extraButtons.push({
          icon: 'error',
          tooltip: 'lang.userEdit.blockUser',
          action: 'block',
          iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
          disabled: !!item.blocked,
        });
      }

      if (this.permissionService.hasPermissions([this.permissionService.Permission.planning_delete])) {
        item.sbButtons.push({
          icon: 'edit',
          link: RoutingID.clientUserEdit,
          pathParams: [{ id: 'clientUserID', value: item?.user?.id }],
          tooltip: 'lang.globals.editUser',
        });
      }
    });
    return result;
  }

  resolveSplitButtonAction(user: UserClientFullDTOAndButtons, $event: SplitButtonAction) {
    switch ($event) {
      case 'block':
        this.blockUser(user);
        break;
      case 'unblock':
        this.unblockUser(user);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  blockUser(user: ManagedUserClientDTO) {
    if (!user) {
      throw new Error('User not found!');
    }
    if (user.blocked) {
      throw new Error('User already blocked!');
    }

    this.modal
      .confirm({
        title: this.translate.instant('lang.userEdit.blockUser'),
        message: this.translate.instant('lang.userEdit.blockUserQuestion'),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (!result) {
          return;
        }
        this.api
          .ca_am_User_blockUser(user)
          .pipe(
            take(1),
            finalize(() => {
              this.requestDebounced();
            })
          )
          .subscribe({
            next: data => {
              user = data;
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      })
      .catch(() => {});
  }

  unblockUser(user: ManagedUserClientDTO) {
    if (!user) {
      throw new Error('User not found!');
    }
    if (!user.blocked) {
      throw new Error('User already unblocked!');
    }

    this.modal
      .confirm({
        title: this.translate.instant('lang.userEdit.unblockUser'),
        message: this.translate.instant('lang.userEdit.unblockUserQuestion'),

        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (!result) {
          return;
        }
        this.api
          .ca_am_User_unblockUser(user)
          .pipe(
            take(1),
            finalize(() => {
              this.requestDebounced();
            })
          )
          .subscribe({
            next: data => {
              user = data;
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      })
      .catch(() => {});
  }

  handleRouteModal() {
    if (this.modalOpen) {
      return;
    }
    let currentRoute = this.activatedRoute.snapshot;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }
    const routingID: RoutingID = currentRoute.data['id'] as RoutingID;
    if (routingID === RoutingID.clientUserCreate) {
      this.modalOpen = true;
      this.modal
        .showNavigationModal({
          entryComponent: { value: ClientUserEditModalComponent, data: {} },
          title: this.translate.instant('lang.adminUserEdit.createUser'),
          modalOptions: { windowClass: 'bigXL' },
        })
        .then((userCreated: boolean) => {
          this.modalOpen = false;
          this.navService.navigateToPoint(RoutingID.clientUserManagement);
          if (userCreated) {
            this.refreshFromServer();
            this.toasterService.toast(ToastType.SUCCESS, this.translate.instant('lang.adminUserEdit.userCreated'));
          }
        })
        .catch(() => {
          this.modalOpen = false;
          this.navService.navigateToPoint(RoutingID.clientUserManagement);
        });
    } else if (routingID === RoutingID.clientUserEdit) {
      this.modalOpen = true;
      const clientUserID = +currentRoute.params['clientUserID'];
      this.modal
        .showNavigationModal({
          entryComponent: { value: ClientUserEditModalComponent, data: { clientUserID } },
          title: this.translate.instant('lang.adminUserEdit.editUser'),
          modalOptions: { windowClass: 'bigXL' },
        })
        .then((userEdited: boolean) => {
          this.modalOpen = false;
          this.navService.navigateToPoint(RoutingID.clientUserManagement);
          if (userEdited) {
            this.refreshFromServer();
            this.toasterService.toast(ToastType.SUCCESS, this.translate.instant('lang.globals.changesSaved'));
          }
        })
        .catch(() => {
          this.modalOpen = false;
          this.navService.navigateToPoint(RoutingID.clientUserManagement);
        });
    }
  }

  // filter panel methods
  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.filteredRoles = filterMap.get('roles');
    this.filteredPermissions = filterMap.get('permissions');
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
    const orderOption = filterMap.get('orderOption')?.[0];
    const orderDirection = filterMap.get('orderDirection')?.[0];
    this.setOrderOptionFromPanel(orderOption, orderDirection);
  }

  private setOrderOptionFromPanel(value: ca_am_User_retrieveAll_order, pDirection: Order) {
    const foundOrderOption = this.allOrderOptions.find(option => option.value === value);
    if (!foundOrderOption) {
      // fallback to default if nothing is set or orderOption cannot be found in allOrderOptions
      this.resetOrderOption();
      return;
    }
    this.orderOption.set({ ...foundOrderOption, direction: pDirection });
  }
}
